import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuthSlice } from './features/auth/authSlice';
import { useGuildsSlice } from './features/guilds/guildsSlice';
import { useUISlice } from './features/ui/uiSlice';
import { useAuthenticateWithDiscordMutation } from './features/auth/authApi';
import { guildsApi, useGetBotGuildsQuery } from './features/guilds/guildsApi';
import { templatesApi } from './features/templates/templatesApi';
import { useValidateMessagesMutation } from './features/templates/templatesApi';
import { initializeDiscordSDK } from './utils/discord';
import { GuildSelector } from './components/guild/GuildSelector';
import { TemplateList } from './components/templates/TemplateList';
import { Dashboard } from './components/dashboard/Dashboard';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { ErrorMessage } from './components/common/ErrorMessage';
import { NoAccess } from './components/common/NoAccess';
import { AppLayout } from './components/layout/AppLayout';

// Get CLIENT_ID from window (injected by server)
declare global {
  interface Window {
    CLIENT_ID: string;
  }
}

export const App: React.FC = () => {
  const authSlice = useAuthSlice();
  const guildsSlice = useGuildsSlice();
  const uiSlice = useUISlice();
  const [authenticateWithDiscord, {
    data: authData,
    error: authError,
    isLoading: isAuthenticating,
    isSuccess: authSuccess
  }] = useAuthenticateWithDiscordMutation();

  // Initialize Discord SDK once on mount
  useEffect(() => {
    if (!window.CLIENT_ID) {
      console.error('No Discord client ID found');
      authSlice.setInitError('Discord client ID not configured');
      return;
    }

    const initializeSDK = async () => {
      authSlice.setInitializing(true);
      try {
        await initializeDiscordSDK(window.CLIENT_ID);
        authSlice.setReady();
      } catch (error) {
        authSlice.setInitError(error instanceof Error ? error.message : 'Failed to initialize Discord SDK');
      }
    };

    void initializeSDK();
  }, []);

  // Update local state when authentication succeeds
  useEffect(() => {
    if (authSuccess && authData) {
      authSlice.setAuth(authData.auth);
      authSlice.setHasAccess(true);
      authSlice.setInitializing(false);
    }
  }, [authSuccess, authData, authSlice]);

  // Automatically attempt authentication when Discord is ready (once)
  useEffect(() => {
    if (authSlice.isReady && !authSlice.isAuthenticated && !isAuthenticating && !authError) {
      void authenticateWithDiscord();
    }
  }, [authSlice.isReady]);

  // Handle authentication errors
  useEffect(() => {
    if (authError) {
      const errorMessage = typeof authError === 'object' && 'error' in authError
        ? String(authError.error)
        : 'Authentication failed';
      authSlice.setInitError(errorMessage);
    }
  }, [authError, authSlice]);

  const dispatch = useDispatch();
  const [validateMessages] = useValidateMessagesMutation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get bot guilds for guild name lookup
  const { data: botGuilds = [] } = useGetBotGuildsQuery();
  const selectedGuildName = guildsSlice.selectedGuild
    ? botGuilds.find(g => g.id === guildsSlice.selectedGuild)?.name ?? null
    : null;

  // Templates requires a selected guild
  const disabledItems = useMemo(
    () => guildsSlice.selectedGuild ? new Set<string>() : new Set(['templates']),
    [guildsSlice.selectedGuild],
  );

  const handleNavigate = (item: string) => {
    if (item === 'dashboard') {
      uiSlice.setView('dashboard');
    } else if (item === 'guilds') {
      uiSlice.setView('guilds');
    } else if (item === 'templates') {
      if (!guildsSlice.selectedGuild) return;
      uiSlice.setView('templates');
    }
  };

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);

    try {
      if (uiSlice.currentView === 'dashboard') {
        // Validate messages first (removes stale), then RTK Query auto-refetches via tag invalidation
        const result = await validateMessages().unwrap();
        if (result.removed > 0) {
          toast.info(`Cleaned up ${result.removed} deleted message${result.removed > 1 ? 's' : ''}`);
        }
      } else if (uiSlice.currentView === 'guilds') {
        dispatch(guildsApi.util.resetApiState());
      } else if (uiSlice.currentView === 'templates') {
        dispatch(templatesApi.util.invalidateTags(['Templates', 'TemplateRoles']));
      }
    } catch {
      toast.error('Refresh failed. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, uiSlice.currentView, validateMessages, dispatch]);

  // Loading, error, and no-access screens render full-bleed (no layout shell).
  // Gate the entire app on auth completion so downstream queries never fire
  // without a token — prevents the cached-401 flash on first paint.
  if (authSlice.isInitializing) {
    return <LoadingSpinner message="Initializing Discord..." />;
  }

  if (authSlice.initError) {
    return <ErrorMessage message={`Initialization failed: ${authSlice.initError}`} />;
  }

  if (!authSlice.hasAccess) {
    return <NoAccess />;
  }

  if (!authSlice.isAuthenticated) {
    return <LoadingSpinner message="Authenticating with Discord..." />;
  }

  // Map current view to nav item key
  const activeItem = uiSlice.currentView;

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      <AppLayout
        activeItem={activeItem}
        onNavigate={handleNavigate}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        disabledItems={disabledItems}
        guildName={selectedGuildName}
        breadcrumb={
          uiSlice.currentView === 'templates' ? (
            <div className="flex items-center gap-4">
              <button
                onClick={() => uiSlice.setView('guilds')}
                className="flex items-center gap-2 text-on-surface-variant hover:text-on-surface transition-colors text-sm font-medium"
              >
                <span className="material-symbols-outlined text-lg">arrow_back</span>
                Back to Servers
              </button>
            </div>
          ) : undefined
        }
      >
        <div key={uiSlice.currentView} className="animate-view-fade-in">
          {uiSlice.currentView === 'dashboard' ? (
            <Dashboard />
          ) : uiSlice.currentView === 'guilds' ? (
            <GuildSelector />
          ) : (
            <TemplateList />
          )}
        </div>
      </AppLayout>
    </>
  );
};
